import React, { useEffect, useState, useMemo, useCallback } from "react";
import Select from "react-select";
import {
  searchUsers,
  getAllUsers,
  deleteUser,
} from "../../../services/admin/userService";
import { getListOptions } from "../../../services/listService";
import useApi from "../../../hooks/useApi";
import useLangNavigate from "../../../hooks/useLangNavigate";
import { usePageTitle } from "../../../hooks/useMeta";
import Table from "../../table/Table";
import DropdownButton from "../../buttons/DropdownButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImageSlash,
  faEllipsisVertical,
} from "@fortawesome/pro-solid-svg-icons";
import DecoratedInput from "../../inputs/DecoratedInput";
import { useTranslation } from "react-i18next";
import NoData from "../../errors/NoData";
import Spinner from "../../utils/Spinner";
import UserAvatar from "../../utils/UserAvatar";
import { toast } from "react-toastify";

const getUserStatus = (user) => {
  if (user.anonymizedAt) return "anonymized";
  if (user.deletedAt) return "closed";
  return "active";
};

export const AdminUsers = ({ tKey = "dashboard.adminUsers." }) => {
  usePageTitle("adminUsers");
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const { request: getUsersRequest, loading: usersLoading } =
    useApi(getAllUsers);
  const { request: searchUsersRequest, loading: searchLoading } =
    useApi(searchUsers);
  const { request: deleteUserRequest } = useApi(deleteUser);
  const { request: getRolesRequest } = useApi(getListOptions);
  const { t } = useTranslation();
  const navigate = useLangNavigate();

  const refreshUsers = useCallback(() => {
    getUsersRequest().then((response) => {
      setAllUsers(response.data);
    });
  }, [getUsersRequest]);

  useEffect(() => {
    refreshUsers();
    getRolesRequest("role").then((response) => {
      setUniqueRoles(
        response.data.map((role) => ({
          value: role.roleId,
          label: t(`utils.roles.${role.label}`),
        }))
      );
    });
  }, [refreshUsers, getRolesRequest, t]);

  useEffect(() => {
    if (!query) {
      setSearchResults(null);
      return;
    }

    searchUsersRequest(query).then((response) => {
      setSearchResults(response.data);
    });
  }, [query, searchUsersRequest]);

  useEffect(() => {
    const baseData = query ? searchResults ?? [] : allUsers;
    const filteredByRole = selectedRole
      ? baseData.filter((user) =>
          user.roles.some((role) => role.roleId === selectedRole.value)
        )
      : baseData;
    const filteredData = statusFilter
      ? filteredByRole.filter(
          (user) => getUserStatus(user) === statusFilter.value
        )
      : filteredByRole;

    setUsers(filteredData);
  }, [allUsers, searchResults, selectedRole, statusFilter, query]);

  const handleUserEdit = useCallback((user) => {
    console.log(user);
  }, []);

  const handleUserDelete = useCallback(
    async (user) => {
      if (user.anonymizedAt) {
        toast.info(t(`${tKey}delete.alreadyAnonymized`));
        return;
      }
      if (
        !window.confirm(
          t(`${tKey}delete.confirm`, {
            fullname: `${user.firstname} ${user.lastname}`,
          })
        )
      ) {
        return;
      }
      try {
        await deleteUserRequest(user.userId);
        toast.success(t(`${tKey}delete.success`));
        refreshUsers();
      } catch (error) {
        toast.error(error.message);
      }
    },
    [deleteUserRequest, refreshUsers, t, tKey]
  );

  const statusOptions = useMemo(
    () => [
      { value: "active", label: t(`${tKey}status.active`) },
      { value: "closed", label: t(`${tKey}status.closed`) },
      { value: "anonymized", label: t(`${tKey}status.anonymized`) },
    ],
    [t, tKey]
  );

  const columns = useMemo(() => {
    let cols = [
      {
        accessor: "userId",
        Cell: "",
      },
      {
        Header: t(`${tKey}image`),
        Cell: ({ row }) =>
          row.original.fullImagePath ? (
            <UserAvatar
              src={row.original.fullImagePath}
              size="60"
              initiales={`${row.original.firstname[0]}${row.original.lastname[0]}`}
            />
          ) : (
            <FontAwesomeIcon icon={faImageSlash} className="text-medium" />
          ),
        disableSortBy: true,
      },
      {
        Header: t(`${tKey}firstname`),
        accessor: "firstname",
      },
      {
        Header: t(`${tKey}lastname`),
        accessor: "lastname",
      },
      {
        Header: t(`${tKey}status.label`),
        accessor: (row) => t(`${tKey}status.${getUserStatus(row)}`),
      },
      {
        Header: t(`${tKey}createdAt`),
        accessor: "createdAt",
        sortType: (rowA, rowB, columnId) => {
          const dateA = new Date(rowA.original.createdAt).getTime();
          const dateB = new Date(rowB.original.createdAt).getTime();

          if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
          if (Number.isNaN(dateA)) return -1;
          if (Number.isNaN(dateB)) return 1;

          if (dateA === dateB) return 0;
          return dateA > dateB ? 1 : -1;
        },
        Cell: ({ value }) => {
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return "";
          return date.toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },
      {
        Header: t(`${tKey}contact`),
        accessor: (row) => (
          <div className="flex column gap-1">
            <a href={`mailto:${row.email}`}>{row.email}</a>
            <span>{row.phone}</span>
          </div>
        ),
      },
      {
        Header: t(`${tKey}roles`),
        accessor: (row) =>
          row.roles.length > 0
            ? row.roles.map((role) => t(`utils.roles.${role.label}`)).join(", ")
            : t(`utils.roles.noRole`),
      },
      {
        Header: "",
        id: "actions",
        Cell: ({ row }) => (
          <DropdownButton
            actions={[
              {
                label: t(`${tKey}actions.details`),
                onClick: () =>
                  navigate(`dashboard/admin-users/${row.original.userId}`),
              },
              {
                label: t(`${tKey}actions.edit`),
                onClick: () => handleUserEdit(row.original),
              },
              {
                label: t(`${tKey}actions.delete`),
                onClick: () => handleUserDelete(row.original),
                disabled: !!row.original.anonymizedAt,
              },
            ]}
          >
            <FontAwesomeIcon color="grey" icon={faEllipsisVertical} />
          </DropdownButton>
        ),
        disableSortBy: true,
      },
    ];
    return cols;
  }, [t, tKey, handleUserEdit, handleUserDelete, navigate]);

  if (usersLoading) return <Spinner />;

  return (
    <div className="dashboard-content">
      <div className="flex gap-1 search-module">
        <DecoratedInput
          className="pl-2 bg-white dashboard-search-bar flex-grow"
          input={{
            id: "searchProduct",
            class: "p4",
            value: "",
            placeholder: t(`${tKey}searchPlaceholder`),
          }}
          hasResearchButton
          onResearchButtonClick={(query) => {
            setQuery(query);
          }}
        />
        <Select
          className="react-custom-select dashboard-select"
          classNamePrefix="react-select"
          placeholder={t(`${tKey}role`)}
          options={uniqueRoles}
          value={selectedRole}
          isClearable
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          onChange={(value) => setSelectedRole(value)}
        />
        <Select
          className="react-custom-select dashboard-select"
          classNamePrefix="react-select"
          placeholder={t(`${tKey}status.label`)}
          options={statusOptions}
          value={statusFilter}
          isClearable
          onChange={(value) => setStatusFilter(value)}
        />
      </div>
      <div className="table-container mt-4">
        {searchLoading ? (
          <Spinner />
        ) : users && users.length > 0 ? (
          <Table
            data={users}
            columns={columns}
            onClickRow={(row) =>
              navigate(`dashboard/admin-users/${row.original.userId}`)
            }
            rowClassName="p-2"
          />
        ) : (
          <NoData />
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
